import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useLazyQuery, useQuery } from "@apollo/client";
import {
  GET_REMEDIATION_DATA_SUPPORTED_DYNAMIC_OPTIONS,
  DEFAULT_ENDPOINT_CONFIG,
  type EndpointConfig,
} from "../TicketingAPI";
import {
  Controller,
  useWatch,
  type UseFormReturn,
} from "react-hook-form";
import GenericField, {
  fieldTypeOptions,
  textEditorFields,
  type GenericFieldProps,
} from "../GenericField/GenericField";
import useSections, {
  type NormalizedField,
} from "../useSections/useSections";
import "./TicketEndpointFields.scss";
import CreateTicketTemplateModal from "./CreateTicketTemplateModal/CreateTicketTemplateModal.js";
import TemplateSelect from "../../Controls/TemplateSelect/TemplateSelect.js";
import type { TemplateSelectOption } from "../../Controls/TemplateSelect/TemplateSelect";
import useTicketTemplate, {
  TemplateModeEnum,
} from "./useTicketTemplate.js";
import type { DisplayedTemplate } from "./useTicketTemplate";
import type { TemplateField } from "./TicketTemplatePreview/TicketTemplatePreview";
import {
  FieldViewBlock,
  Loader,
  TextButton,
  useIcon,
  AlertIndicator,
  REQUIRED_ERROR_MESSAGE,
  validateTextEditorValue,
} from "@SeemplicityDev/shared";

const AUTOCOMPLETE_TRIGGER_CHAR = "{";

// A selected template carried on the `template` form field. Aligned with the
// `DisplayedTemplate` shape the template hook applies, so the value read off the
// form can be passed straight to `applyTemplateValues`.
type SelectedTemplate = DisplayedTemplate;

// Field descriptor produced by `useSections` and consumed by `getField`.
// `mapping`/`query`/`options`/`defaultValue` mirror exactly what `GenericField`
// accepts so they can be forwarded without casts.
interface EndpointField {
  name: string;
  input?: string;
  label?: string;
  size?: string;
  description?: string;
  mapping?: GenericFieldProps["mapping"];
  query?: GenericFieldProps["query"];
  creatable?: boolean;
  required?: boolean;
  defaultValue?: GenericFieldProps["defaultValue"];
  options?: GenericFieldProps["options"];
}

// Modal descriptor passed to the host's `openModal`.
interface OpenModalArgs {
  title: string;
  size: string;
  overlay: boolean;
  component: React.ReactNode;
}

export interface TicketEndpointFieldsProps {
  context?: string;
  onGetProps?: (getProps: unknown) => void;
  keysObject?: unknown;
  fieldMappingId?: string;
  onData?: () => void;
  onLoading?: (loading: boolean) => void;
  filters_config?: unknown;
  filters_config_for_dynamic_mapping?: unknown;
  ignoreDynamicFields?: boolean;
  ticketProviderId?: string;
  formMethods: UseFormReturn;
  initialValues?: Record<string, unknown> | null;
  disableInitialValues?: boolean;
  externalEndpointId?: string | null;
  fetchPolicy?: string;
  showTicketTemplates?: boolean;
  openModal?: (args: OpenModalArgs) => void;
  closeModal?: () => void;
  getFieldsMutationArray?: (
    formValues: Record<string, unknown>
  ) => Array<{ key: string; value: unknown }>;
  onCreateTicketTemplateSuccess?: (name?: string) => void;
  onCreateTicketTemplateError?: (error: unknown) => void;
  templateId?: number | string;
  onEventTriggered?: (...args: unknown[]) => void;
  isOnlyTemplateFields?: boolean;
  viewMode?: boolean;
  endpointConfig?: EndpointConfig;
}

const TicketEndpointFields: React.FC<TicketEndpointFieldsProps> = ({
  context,
  onGetProps,
  keysObject,
  fieldMappingId,
  onData = () => {},
  onLoading = () => {},
  filters_config,
  filters_config_for_dynamic_mapping,
  ignoreDynamicFields = false,
  ticketProviderId,
  formMethods,
  initialValues,
  disableInitialValues = false,
  externalEndpointId = null,
  fetchPolicy = "cache-first",
  showTicketTemplates = false,
  openModal,
  closeModal = () => {},
  getFieldsMutationArray,
  onCreateTicketTemplateSuccess = () => {},
  onCreateTicketTemplateError = () => {},
  templateId,
  onEventTriggered = () => {},
  isOnlyTemplateFields = false,
  viewMode = false,
  endpointConfig = DEFAULT_ENDPOINT_CONFIG,
}) => {
  const {
    staticFieldsQuery,
    dynamicFieldsQuery,
    endpointFieldsQuery,
    staticKey,
    dynamicKey,
    endpointFieldsKey,
  } = endpointConfig;
  const [fieldsSections, setFieldsSections] = useState<unknown>(null);
  const [useSeemplicityDefaultValues, setUseSeemplicityDefaultValues] =
    useState(false);
  const [templateFields, setTemplateFields] = useState<TemplateField[] | null>(
    null
  );

  const { getSvg } = useIcon();

  const {
    control,
    setValue,
    getValues,
    reset: formReset,
    formState: { errors },
  } = formMethods;
  const selectedTemplate = useWatch({
    control,
    name: `template`,
  }) as SelectedTemplate | undefined;

  const { data: staticFieldsData, loading: staticFieldsLoading } =
    useQuery(staticFieldsQuery);

  const { data: dynamicFieldsData, loading: dynamicFieldsLoading } = useQuery(
    dynamicFieldsQuery,
    {
      skip: ignoreDynamicFields,
    }
  );

  const [fetchDynamicOptions] = useLazyQuery(
    GET_REMEDIATION_DATA_SUPPORTED_DYNAMIC_OPTIONS
  );

  const {
    loading: ticketEndpointFieldsDataLoading,
    error: ticketEndpointFieldsDataError,
  } = useQuery(endpointFieldsQuery, {
    fetchPolicy: fetchPolicy as never,
    variables: {
      ticket_provider_id: ticketProviderId,
      selected_keys:
        !fieldMappingId || useSeemplicityDefaultValues
          ? JSON.stringify(keysObject)
          : undefined,
      endpoint_fields_context: context,
      field_mapping_id: !useSeemplicityDefaultValues
        ? fieldMappingId
        : undefined,
      filters_config,
      initial_values: JSON.stringify(initialValues ? initialValues : {}),
      disable_initial_values: disableInitialValues,
      partner_external_id: externalEndpointId,
      ticket_template_id: !useSeemplicityDefaultValues ? templateId : undefined,
      show_template_fields: showTicketTemplates,
    },
    skip: !keysObject && !fieldMappingId && !isOnlyTemplateFields,
    onCompleted: (data) => {
      setFieldsSections(data?.[endpointFieldsKey]?.fields_sections);

      if (!!data?.[endpointFieldsKey]?.ticket_template_fields) {
        setTemplateFields(
          // `formatTemplateFields` returns `TemplateField[] | undefined`; the
          // state holds `TemplateField[] | null`.
          formatTemplateFields(
            data?.[endpointFieldsKey]?.ticket_template_fields
          ) ?? null
        );
      }
    },
  });

  useEffect(() => {
    if (!!selectedTemplate && !!templateFields && !selectedTemplate?.fields) {
      setValue("template", {
        ...selectedTemplate,
        fields: templateFields,
      });
    }
  }, [selectedTemplate, templateFields]);

  const autoCompleteSettings = useMemo(() => {
    return {
      char: AUTOCOMPLETE_TRIGGER_CHAR,
      showFullValueTooltip: true,
      getAutocompleteOptions: async (query: string, selecteItem: string) => {
        let searchedSuggestions = [
          ...staticFieldsData?.[staticKey]?.variables,
          ...(ignoreDynamicFields
            ? []
            : dynamicFieldsData?.[dynamicKey]?.variables),
        ];
        if (!!selecteItem && !ignoreDynamicFields) {
          const suggestionsByField = await fetchDynamicOptions({
            variables: {
              filters_config: filters_config_for_dynamic_mapping,
              field_name: selecteItem,
              search_string: query,
            },
          });
          searchedSuggestions =
            suggestionsByField?.data?.dynamic_remediation_variables_options
              ?.variables;
        }

        const returnedSuggestions = searchedSuggestions?.filter(
          (suggestion: string) => {
            return suggestion?.toLowerCase()?.includes(query?.toLowerCase());
          }
        );

        return returnedSuggestions;
      },
      checkShouldKeepType: (value: string) =>
        !ignoreDynamicFields &&
        dynamicFieldsData?.[dynamicKey]?.variables.includes(value),
    };
  }, [
    staticFieldsData,
    dynamicFieldsData,
    ignoreDynamicFields,
    filters_config_for_dynamic_mapping,
    fetchDynamicOptions,
    staticKey,
    dynamicKey,
  ]);

  const getField = (field: EndpointField) => {
    return (
      <Controller
        shouldUnregister={false}
        name={`fields.${field?.name}`}
        render={({
          field: { onChange, onBlur, value },
          fieldState: { isTouched },
        }) => {
          const templateField = selectedTemplate?.fields?.find(
            (templateField) =>
              templateField.key === field.name && templateField.isCompatible
          );
          let fieldOptions = field.options;
          if (templateField && field.input === fieldTypeOptions.SELECT_QUERY) {
            // `templateField.value` is `unknown` at the template boundary; the
            // SELECT_QUERY option id is a string at runtime.
            const templateOptionValue = templateField.value as
              | string
              | undefined;
            if (!field.options) {
              fieldOptions = [
                {
                  id: templateOptionValue,
                  name: templateField.displayValue,
                },
              ];
            } else if (
              !field.options?.find((opt) => opt.id === templateOptionValue)
            ) {
              fieldOptions = [
                ...(field.options ?? []),
                {
                  id: templateOptionValue,
                  name: templateField.displayValue,
                },
              ];
            }
          }
          return (
            <GenericField
              {...formMethods}
              onChange={onChange}
              name={field?.name}
              registerPrefix={`fields.${field?.name}`}
              onBlur={onBlur}
              value={value}
              size={field?.size}
              clearable
              description={field?.description}
              setSingleOption={false}
              error={
                isTouched
                  ? // `errors.fields` is a RHF FieldError/Merge union that has no
                    // string index signature; the per-field error is keyed by
                    // field name at runtime. `GenericField.error` is `unknown`.
                    (errors?.fields as Record<string, unknown> | undefined)?.[
                      field?.name
                    ]
                  : null
              }
              mapping={field?.mapping}
              query={field?.query}
              creatable={field?.creatable}
              key={field?.name}
              input={field?.input}
              label={field?.label}
              options={fieldOptions}
              placeholder={`Select${field?.creatable ? " or Create" : ""} ${
                field?.label
              }`}
              disabled={!!templateField}
              required={field?.required}
              defaultValue={field?.defaultValue}
              hint="Start typing or enter { for field list"
              hideLabelHelper={false}
              autoCompleteSettings={autoCompleteSettings}
              onSelectedOptionObjectChange={onSelectedOptionObjectChange}
            />
          );
        }}
        rules={{
          ...(field?.required && { required: REQUIRED_ERROR_MESSAGE }),
          validate:
            field.required &&
            // `textEditorFields` is a readonly tuple of specific literals; widen
            // it to a string array to test membership (original passed unchecked).
            (textEditorFields as readonly string[]).includes(
              field?.input as string
            )
              ? validateTextEditorValue
              : () => true,
        }}
        control={control}
      />
    );
  };

  const { getSections, defaultValues, getFieldPropsByFieldName } =
    useSections<NormalizedField>(
      // `fieldsSections` is the raw `fields_sections` payload (typed `unknown`);
      // cast to the hook's first-param type (`RawSection[]` is not exported).
      fieldsSections as Parameters<typeof useSections>[0],
      onGetProps,
      // `getField` reads a narrower `EndpointField` view of the normalized field;
      // the runtime field carries those members. Cast keeps the hook's `TField`
      // pinned to `NormalizedField` (avoids inferring an unsatisfiable constraint).
      getField as (field: NormalizedField) => React.ReactNode
    );

  const {
    onSelectTemplateToDisplay,
    showTemplate,
    displayedTemplate,
    templates,
    getFieldsTemplatePreview,
    onSelectedOptionObjectChange,
    applyTemplateValues,
    onNewTemplateCreated,
    formatTemplateFields,
  } = useTicketTemplate(
    ticketProviderId,
    keysObject,
    formMethods,
    fetchPolicy,
    onData,
    onCreateTicketTemplateSuccess,
    // Hook requires a non-optional builder; default to an empty result when the
    // optional prop is absent (preserves the original unguarded call semantics).
    getFieldsMutationArray ?? (() => []),
    // `getFieldPropsByFieldName` returns `NormalizedField`; the hook reads it as
    // `GenericFieldProps` (input/options/label/mapping/name/creatable), all of
    // which the normalized field carries at runtime.
    getFieldPropsByFieldName as (
      fieldName: string
    ) => GenericFieldProps | undefined,
    showTicketTemplates
  );

  useEffect(() => {
    formReset({ ...getValues(), fields: defaultValues });
    onData();
  }, [defaultValues]);

  useEffect(() => {
    onLoading(ticketEndpointFieldsDataLoading);
  }, [ticketEndpointFieldsDataLoading]);

  useEffect(() => {
    if (!!templates) {
      const defaultTemplate = templates?.find((t) => t.id === templateId);

      if (!!defaultTemplate) {
        setValue("template", defaultTemplate);
      }
    }
  }, [templates]);

  const getFieldsTitles = useCallback(() => {
    if (!!getValues && !!getFieldPropsByFieldName) {
      const fields = getValues("fields");

      if (!!fields) {
        return (
          <div className="view-mode-fields">
            {Object.keys(fields)
              ?.map((field) => getFieldPropsByFieldName(field)?.label)
              ?.join(", ")}
          </div>
        );
      }
    }
  }, [getValues, getFieldPropsByFieldName]);

  return viewMode ? (
    <>
      {showTicketTemplates && (
        <FieldViewBlock
          label="Use a Template"
          text={selectedTemplate ? "Yes" : "No"}
        />
      )}
      <FieldViewBlock
        label="Ticket Fields"
        // `getFieldsTitles` returns a JSX element; `FieldViewBlock.text` is typed
        // `string` but renders any ReactNode. Cast preserves the original runtime.
        text={getFieldsTitles() as unknown as string}
      />
    </>
  ) : (
    <div className={"endpoint-list"}>
      {ticketEndpointFieldsDataLoading ||
      staticFieldsLoading ||
      dynamicFieldsLoading ? (
        <Loader
          flexcenter
          fullHeight
          img={getSvg("icon-spinner")}
          title={"Retrieving your mapping data fields"}
          subtitle={"This may take a few moments"}
        />
      ) : ticketEndpointFieldsDataError ? (
        <Loader center subtitle={ticketEndpointFieldsDataError?.message} />
      ) : showTicketTemplates ? (
        <div className={"rmq-form-ticket-fields-wrapper"}>
          <TextButton
            label={"Reset to Seemplicity Default"}
            leftIcon={"seem-icon-reset"}
            buttonStyle={"textLink"}
            type={"button"}
            wrapperClassName={"reset-btn"}
            data-analytics-event="user_click"
            data-analytics-props={`{"name": "reset_to_default_template_btn"}`}
            onClick={() => {
              if (useSeemplicityDefaultValues) {
                formReset({ ...getValues(), fields: defaultValues });
              } else {
                setUseSeemplicityDefaultValues(true);
              }
              setValue("template", null);
            }}
          />
          <Controller
            name={`template`}
            render={({ field: { onChange, value } }) => {
              return (
                <TemplateSelect
                  label={"Templates"}
                  name={"template"}
                  placeholder={"Select a template"}
                  labelTop
                  data-testid={"template"}
                  withHelp={
                    "Use templates to quickly create tickets. Based on your selected Issue Type, the most compatible templates display at the top."
                  }
                  value={value}
                  // `templates` (TemplateSummary[]) has optional `id`; the select
                  // option requires it. Templates always carry an id at runtime.
                  options={templates as TemplateSelectOption[] | undefined}
                  data-analytics-event="user_click"
                  data-analytics-props={`{"name": "template_dropdown"}`}
                  onChange={(template?: TemplateSelectOption) => {
                    if (!!template) {
                      onChange(template);
                    } else {
                      setValue("template", null);
                    }
                  }}
                  onApplyTemplateData={(templateData: TemplateSelectOption) => {
                    applyTemplateValues(templateData as SelectedTemplate);
                  }}
                  onCopyTemplateData={(templateData: TemplateSelectOption) => {
                    applyTemplateValues(
                      templateData as SelectedTemplate,
                      TemplateModeEnum.COPY
                    );
                  }}
                  onSelectTemplateToDisplay={onSelectTemplateToDisplay}
                  isGenericAreaLoading={!displayedTemplate}
                  onEventTriggered={onEventTriggered}
                >
                  {showTemplate()}
                </TemplateSelect>
              );
            }}
            control={control}
          />
          <div className={"rmq-form-ticket-fields"}>{getSections}</div>
          {!selectedTemplate && (
            <AlertIndicator
              hasMaxWidth={false}
              content={
                <span className={"save-template-alert"}>
                  Save ticket configuration as a Template for future use, reuse,
                  and updates
                  <TextButton
                    label={"Save as Template"}
                    buttonStyle={"textLink"}
                    type={"button"}
                    data-testid={"open-new-template-modal"}
                    data-analytics-event="user_click"
                    data-analytics-props={`{"name": "banner_save_as_template_btn"}`}
                    onClick={() => {
                      const fields = getFieldsTemplatePreview();
                      openModal?.({
                        title: "Save as Template",
                        size: "m",
                        overlay: true,
                        component: (
                          <CreateTicketTemplateModal
                            keys={keysObject}
                            // `TemplateSummary.name` is optional; the modal's
                            // summary requires it. Templates always have a name.
                            templates={
                              templates as { name: string }[] | undefined
                            }
                            fields={fields}
                            // `ticketProviderId` is optional on this component but
                            // required by the modal; it's always set when the
                            // template flow (showTicketTemplates) is reachable.
                            ticketProviderId={ticketProviderId as string}
                            // `onNewTemplateCreated` accepts the created template
                            // summary; the modal's success payload is the same
                            // object enriched with id/fields at runtime.
                            onSuccess={
                              // Payload `fields` are loosely typed Records here but
                              // are `TemplateField[]` at runtime; `TemplateSummary`
                              // and the modal payload describe the same object, so
                              // route through `unknown` to bridge the field shapes.
                              onNewTemplateCreated as unknown as (payload: {
                                name: string;
                                id: string | undefined;
                                fields: Array<Record<string, unknown>>;
                              }) => void
                            }
                            onError={onCreateTicketTemplateError}
                            closeModal={closeModal}
                            onEventTriggered={onEventTriggered}
                          />
                        ),
                      });
                    }}
                  />
                </span>
              }
            ></AlertIndicator>
          )}
        </div>
      ) : (
        <> {getSections} </>
      )}
    </div>
  );
};

export default TicketEndpointFields;
